import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Lightbulb, Wrench, Search, Heart, Target, Megaphone, LogOut, User, Shield, CreditCard, Plus, Download, ChevronDown, Briefcase, FileText, HelpCircle } from 'lucide-react';
import PurchaseCreditsModal from '../components/PurchaseCreditsModal';

const DashboardPage = ({ auth }) => {
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [userCredits, setUserCredits] = useState(0);

  useEffect(() => {
    fetchResults();
    fetchUserData();
  }, []);

  const fetchResults = async () => {
    try {
      const response = await axios.get(`${API}/assessment/result/${auth.user.id}`);
      setResult(response.data);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load results');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUserCredits(response.data.credits || 0);
    } catch (error) {
      console.error('Failed to fetch user data');
    }
  };

  const handleLogout = () => {
    auth.logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const handleDownloadMinistryPDF = async () => {
    try {
      const response = await axios.get(`${API}/assessment/download-ministry-pdf/${auth.user.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BUILDR_Ministry_Report_${auth.user.name.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Ministry report downloaded!');
    } catch (error) {
      toast.error('Failed to download Ministry PDF');
    }
  };

  const handleDownloadMarketplacePDF = async () => {
    try {
      const response = await axios.get(`${API}/assessment/download-marketplace-pdf/${auth.user.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BUILDR_Marketplace_Report_${auth.user.name.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Marketplace report downloaded!');
    } catch (error) {
      toast.error('Failed to download Marketplace PDF');
    }
  };

  const handleDownloadCompletePDF = async () => {
    try {
      const response = await axios.get(`${API}/assessment/download-complete-pdf/${auth.user.id}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `BUILDR_Complete_Report_${auth.user.name.replace(' ', '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Complete report downloaded!');
    } catch (error) {
      toast.error('Failed to download Complete PDF');
    }
  };

  const dimensionIcons = {
    B: Lightbulb,
    U: Wrench,
    I: Search,
    L: Heart,
    D: Target,
    R: Megaphone
  };

  const dimensionColors = {
    B: { bg: 'bg-purple-100', text: 'text-purple-700' },
    U: { bg: 'bg-blue-100', text: 'text-blue-700' },
    I: { bg: 'bg-green-100', text: 'text-green-700' },
    L: { bg: 'bg-pink-100', text: 'text-pink-700' },
    D: { bg: 'bg-orange-100', text: 'text-orange-700' },
    R: { bg: 'bg-red-100', text: 'text-red-700' }
  };

  const dimensionNames = {
    B: 'Brainstorm',
    U: 'Uncover',
    I: 'Inspect',
    L: 'Lift',
    D: 'Deliver',
    R: 'Rally'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-8 px-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2" data-testid="dashboard-title">Welcome, {auth.user.name}!</h1>
            <p className="text-gray-600">Your BUILDR Assessment Dashboard</p>
          </div>
          <div className="flex gap-2">
            {auth.user.role === 'admin' && (
              <Button
                data-testid="admin-dashboard-link"
                onClick={() => navigate('/admin')}
                variant="outline"
                className="flex items-center gap-2"
              >
                <Shield className="h-4 w-4" />
                Admin Dashboard
              </Button>
            )}
            <Button
              data-testid="help-btn"
              onClick={() => navigate('/help')}
              variant="outline"
              className="flex items-center gap-2"
            >
              <HelpCircle className="h-4 w-4" />
              Help
            </Button>
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Profile Card */}
        <Card className="mb-8 shadow-lg" data-testid="profile-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-2xl">{auth.user.name}</CardTitle>
                  <CardDescription className="text-base">{auth.user.email}</CardDescription>
                  <div className="flex gap-2 mt-2">
                    <Badge>{auth.user.role === 'admin' ? 'Administrator' : 'Volunteer'}</Badge>
                    {auth.user.organization_name && (
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-300">
                        {auth.user.organization_name}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-indigo-600" />
                  <span className="text-2xl font-bold text-indigo-600" data-testid="user-credits">{userCredits}</span>
                  <span className="text-gray-600">Credit{userCredits !== 1 ? 's' : ''}</span>
                </div>
                <Button
                  data-testid="buy-credits-btn"
                  onClick={() => setShowPurchaseModal(true)}
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Buy Credits
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {!result ? (
          // No Assessment Taken
          <Card className="text-center py-12 shadow-lg">
            <CardHeader>
              <CardTitle className="text-3xl mb-4">Take Your BUILDR Assessment</CardTitle>
              <CardDescription className="text-lg mb-6">
                Discover your unique strengths and find the perfect role in your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              {userCredits > 0 ? (
                <Button
                  data-testid="start-assessment-btn"
                  onClick={() => navigate('/assessment')}
                  size="lg"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg"
                >
                  Start Assessment
                </Button>
              ) : (
                <div>
                  <p className="text-lg text-gray-600 mb-6">You need 1 credit to take the assessment</p>
                  <Button
                    data-testid="purchase-to-start-btn"
                    onClick={() => setShowPurchaseModal(true)}
                    size="lg"
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg"
                  >
                    Purchase Assessment - $10
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          // Assessment Results
          <div className="space-y-6">
            {/* Pairing Code Hero */}
            <Card className="shadow-lg border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50" data-testid="pairing-code-card">
              <CardContent className="pt-6 pb-6">
                <div className="text-center">
                  <Badge className="bg-indigo-600 text-white text-sm px-4 py-1 mb-3">Your BUILDR Pairing</Badge>
                  <div className="flex items-center justify-center gap-4 mb-3">
                    {(() => {
                      const PrimaryIcon = dimensionIcons[result.primary_dimension];
                      const SecondaryIcon = dimensionIcons[result.secondary_dimension];
                      const pairingCode = [result.primary_dimension, result.secondary_dimension].sort().join('');
                      return (
                        <>
                          <div className={`w-12 h-12 ${dimensionColors[result.primary_dimension].bg} rounded-full flex items-center justify-center`}>
                            <PrimaryIcon className={`w-6 h-6 ${dimensionColors[result.primary_dimension].text}`} />
                          </div>
                          <h2 className="text-6xl font-bold text-indigo-700" data-testid="pairing-code-display">{pairingCode}</h2>
                          <div className={`w-12 h-12 ${dimensionColors[result.secondary_dimension].bg} rounded-full flex items-center justify-center`}>
                            <SecondaryIcon className={`w-6 h-6 ${dimensionColors[result.secondary_dimension].text}`} />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                  <div className="flex items-center justify-center gap-2 text-gray-600">
                    <span className={`font-semibold ${dimensionColors[result.primary_dimension].text}`}>{dimensionNames[result.primary_dimension]}</span>
                    <span>+</span>
                    <span className={`font-semibold ${dimensionColors[result.secondary_dimension].text}`}>{dimensionNames[result.secondary_dimension]}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-lg" data-testid="results-summary-card">
              <CardHeader>
                <CardTitle className="text-2xl">Your Strength Breakdown</CardTitle>
                <CardDescription>Your primary and secondary BUILDR dimensions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Primary Dimension */}
                  <div className={`p-6 ${dimensionColors[result.primary_dimension].bg} rounded-lg border-2 ${dimensionColors[result.primary_dimension].text.replace('text', 'border')}`}>
                    <Badge className="mb-3">Primary Strength</Badge>
                    <div className="flex items-center gap-3 mb-2">
                      {(() => {
                        const Icon = dimensionIcons[result.primary_dimension];
                        return <Icon className={`w-8 h-8 ${dimensionColors[result.primary_dimension].text}`} />;
                      })()}
                      <h3 className="text-2xl font-bold">{dimensionNames[result.primary_dimension]}</h3>
                    </div>
                    <p className="text-sm">
                      {result.primary_dimension === 'B' && 'Visionary thinking and asking big questions'}
                      {result.primary_dimension === 'U' && 'Creating and improving solutions'}
                      {result.primary_dimension === 'I' && 'Evaluating and making sound decisions'}
                      {result.primary_dimension === 'L' && 'Supporting and empowering others'}
                      {result.primary_dimension === 'D' && 'Driving results and execution'}
                      {result.primary_dimension === 'R' && 'Motivating and mobilizing people'}
                    </p>
                  </div>

                  {/* Secondary Dimension */}
                  <div className={`p-6 ${dimensionColors[result.secondary_dimension].bg} rounded-lg border`}>
                    <Badge className="mb-3" variant="outline">Secondary Strength</Badge>
                    <div className="flex items-center gap-3 mb-2">
                      {(() => {
                        const Icon = dimensionIcons[result.secondary_dimension];
                        return <Icon className={`w-8 h-8 ${dimensionColors[result.secondary_dimension].text}`} />;
                      })()}
                      <h3 className="text-2xl font-bold">{dimensionNames[result.secondary_dimension]}</h3>
                    </div>
                    <p className="text-sm">
                      {result.secondary_dimension === 'B' && 'Visionary thinking and asking big questions'}
                      {result.secondary_dimension === 'U' && 'Creating and improving solutions'}
                      {result.secondary_dimension === 'I' && 'Evaluating and making sound decisions'}
                      {result.secondary_dimension === 'L' && 'Supporting and empowering others'}
                      {result.secondary_dimension === 'D' && 'Driving results and execution'}
                      {result.secondary_dimension === 'R' && 'Motivating and mobilizing people'}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 justify-center">
                  <Button
                    data-testid="view-full-results-btn"
                    onClick={() => navigate('/results')}
                    size="lg"
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    View Full Results
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        data-testid="download-pdf-btn"
                        variant="outline"
                        size="lg"
                        className="border-green-600 text-green-600 hover:bg-green-50"
                      >
                        <Download className="mr-2 h-5 w-5" />
                        Download Report
                        <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="center" className="w-56">
                      <DropdownMenuItem onClick={handleDownloadMinistryPDF} className="cursor-pointer">
                        <Heart className="mr-2 h-4 w-4 text-indigo-600" />
                        <span>Ministry Report</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDownloadMarketplacePDF} className="cursor-pointer">
                        <Briefcase className="mr-2 h-4 w-4 text-emerald-600" />
                        <span>Marketplace Report</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDownloadCompletePDF} className="cursor-pointer">
                        <FileText className="mr-2 h-4 w-4 text-purple-600" />
                        <span>Complete Report (Both)</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <Button
                    data-testid="retake-btn"
                    onClick={() => navigate('/assessment')}
                    variant="outline"
                    size="lg"
                  >
                    Retake Assessment
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Purchase Credits Modal */}
      {showPurchaseModal && (
        <PurchaseCreditsModal onClose={() => setShowPurchaseModal(false)} />
      )}
    </div>
  );
};

export default DashboardPage;
